/**
 * Sits inside a required personalization field. While the field is empty it marks the section
 * (or quick view dialog), which disables the buy buttons (see the block's stylesheet), and stops
 * the product form from being sent. It is rendered only for variants that need personalizing, so
 * switching to another variant removes it and the buttons come back.
 *
 * It listens at the section level and looks the field up each time, because quick view and the
 * variant picker can swap the input element out after this has connected.
 */
class PersonalizationGuard extends HTMLElement {
  connectedCallback() {
    this.root = this.closest('dialog, .shopify-section') || document.body;
    this.root.addEventListener('input', this.#onInput);
    this.root.addEventListener('submit', this.#onSubmit, true);
    this.#update();
  }

  disconnectedCallback() {
    this.root?.removeEventListener('input', this.#onInput);
    this.root?.removeEventListener('submit', this.#onSubmit, true);
    if (!this.root?.querySelector('personalization-guard')) this.#setMissing(false);
  }

  get #input() {
    return (
      this.closest('.personalization-field')?.querySelector('.personalization-field__input') ||
      this.root?.querySelector('.personalization-field__input')
    );
  }

  get #missing() {
    const input = this.#input;
    return !input || input.value.trim() === '';
  }

  #setMissing(missing) {
    this.root?.toggleAttribute('data-personalization-missing', missing);
    this.root
      ?.querySelectorAll('.buy-buttons-block .add-to-cart-button, .sticky-add-to-cart__button')
      .forEach((button) => (missing ? button.setAttribute('aria-disabled', 'true') : button.removeAttribute('aria-disabled')));
  }

  #update = () => this.#setMissing(this.#missing);

  /** @param {Event} event */
  #onInput = (event) => {
    if (event.target instanceof Element && event.target.matches('.personalization-field__input')) this.#update();
  };

  /** @param {SubmitEvent} event */
  #onSubmit = (event) => {
    if (!this.#missing) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.#input?.focus();
  };
}

if (!customElements.get('personalization-guard')) customElements.define('personalization-guard', PersonalizationGuard);
